// ============================================
// src/App/suppressedSkillsPullMerge.js
// MA-395 (suppressed-skill-ids-cloud-sync slice) — the decision logic for
// App.jsx's cloud->local merge effect's suppressedSkillIds field. Mirrors
// assignedSkillsPullMerge.js's stamp-based LWW shape exactly (local wins
// when stamped strictly newer; a never-stamped device unions, no
// regression; otherwise cloud wins EXACTLY, which is what lets a
// RESTORE — un-suppress — propagate as an array SHRINK, the classic
// union-only trap). No cloudMirror field: unlike assignedSkillIds,
// suppressedSkillIds has no second cloud writer to reconcile against, so
// there is nothing for a future push to diff against.
//
// MERGE-POLICY DECISION: the never-stamped branch UNIONS (does not adopt
// cloud exactly), matching assignedSkillsPullMerge's own branch. Unlike
// assignedSkillIds, this field never cloud-synced before AT ALL, so there is
// no literal pre-existing behavior to preserve here -- but MA-89 shipped
// suppressedSkillIds fully LOCAL-ONLY well before this slice, so at the
// moment this migration ships every existing user's device is, by
// definition, in the "never stamped" state with REAL suppression data
// already on disk. Adopting cloud EXACTLY on that first sync would silently
// RESURFACE a skill the parent deliberately hid, the very first time that
// device talks to the cloud -- a worse trust break than the symmetric cost
// on the assign side (an un-assign taking one extra pull to propagate,
// documented below). See suppressedSkills.ts's own header ("a suppression
// stays until the parent explicitly restores it -- a standing preference,
// not a one-shot nudge") for why silently un-hiding is the wrong default
// here. Concretely this costs the SAME bounded "one extra local pull"
// latency for a restore to propagate that assignedSkillsPullMerge.js
// already documents and accepts for an un-assign.
//
// RESIDUE (named, not fixed this slice): a never-stamped device's
// shouldPushLocal is always false in the union branch, so a suppression
// added entirely offline, on a device that never directly uses the suppress
// toggle again, has no path to reach the cloud until SOME local edit stamps
// suppressedSkillIdsUpdatedAt on that device. This mirrors the identical
// residue assignedSkillsPullMerge.js already accepts for the assign queue.
// ============================================

import { normalizeSuppressedSet, mergeSuppressedSets } from "../lib/engine/suppressedSkills";

/**
 * @param {{
 *   localIds: unknown,
 *   localStamp: string | null | undefined,
 *   cloudIds: unknown,
 *   cloudStamp: string | null | undefined,
 * }} args
 * @returns {{
 *   suppressedSkillIds: string[],
 *   suppressedSkillIdsUpdatedAt: string | null,
 *   shouldPushLocal: boolean,
 *   changed: boolean,
 * }}
 */
export function resolveSuppressedSkillsPullMerge({ localIds, localStamp, cloudIds, cloudStamp }) {
  const local = normalizeSuppressedSet(localIds);
  const cloud = normalizeSuppressedSet(cloudIds);

  const localIsNewer = !!localStamp && (!cloudStamp || localStamp > cloudStamp);
  if (localIsNewer) {
    const suppressedSkillIds = mergeSuppressedSets(local, cloud);
    return {
      suppressedSkillIds,
      suppressedSkillIdsUpdatedAt: localStamp,
      shouldPushLocal: true,
      changed: !sameSet(suppressedSkillIds, local),
    };
  }

  if (!localStamp) {
    // Never-stamped device: union (see header rationale above — avoids
    // ever silently un-hiding a parent's real, un-synced suppression).
    const suppressedSkillIds = mergeSuppressedSets(cloud, local);
    return {
      suppressedSkillIds,
      suppressedSkillIdsUpdatedAt: cloudStamp || null,
      shouldPushLocal: false,
      changed: !sameSet(suppressedSkillIds, local),
    };
  }

  // Cloud is stamped same-or-newer than this device's own last edit: trust
  // it EXACTLY — this is the branch that lets a restore (un-suppress)
  // propagate as an array SHRINK.
  return {
    suppressedSkillIds: cloud,
    suppressedSkillIdsUpdatedAt: cloudStamp,
    shouldPushLocal: false,
    changed: !sameSet(cloud, local),
  };
}

// Set-equality (unordered) — appropriate here since suppressedSkillIds is a
// membership set, not an ordered queue (see suppressedSkills.ts's own
// "MEMBERSHIP, not order" contract). Unlike assignedSkillsPullMerge.js's
// positional sameQueue, order must never affect this comparison.
function sameSet(a, b) {
  if (a.length !== b.length) return false;
  const bSet = new Set(b);
  return a.every((id) => bSet.has(id));
}
